import { Decimal, allocateCents, dec, minorUnits } from '../money.js';
import { KeelError, assertNever } from '../errors.js';
import { AU_INDEXATION_CUTOFF } from '../tax/au.js';
import { jurisdictionFor } from '../tax/jurisdiction.js';
/** Decimal places kept for unit quantities produced by ratios (splits, demergers…). */
export const UNIT_DP = 10;
export const unitsToString = (d) => {
    const r = new Decimal(d).toDecimalPlaces(UNIT_DP, Decimal.ROUND_HALF_EVEN);
    return r.isZero() ? '0' : r.toFixed();
};
export const roundCents = (d) => new Decimal(d).toDecimalPlaces(0, Decimal.ROUND_HALF_EVEN).toNumber();
/**
 * Security-currency minor units (fractional ok) → base-currency minor units, unrounded. `fxRate` null is only valid when
 * the amount is already in the base currency; otherwise throws VALIDATION (never silently mix currencies).
 */
export const toBaseDecimal = (amount, ccy, fxRate, baseCcy = 'AUD') => {
    if (fxRate == null) {
        if (ccy.toUpperCase() !== baseCcy.toUpperCase())
            throw new KeelError('VALIDATION', `fxRate is required to convert ${ccy} to ${baseCcy}`);
        return dec(amount);
    }
    return dec(amount).div(10 ** minorUnits(ccy)).mul(fxRate).mul(10 ** minorUnits(baseCcy));
};
/** As `toBaseDecimal`, rounded half-even to whole minor units. */
export const toBaseCents = (cents, ccy, fxRate, baseCcy = 'AUD') => roundCents(toBaseDecimal(cents, ccy, fxRate, baseCcy));
export const isOpenParcel = (p) => dec(p.remainingUnits).gt(0);
const byDateThenId = (a, b) => a.acquiredDate === b.acquiredDate ? (a.id < b.id ? -1 : a.id > b.id ? 1 : 0) : a.acquiredDate < b.acquiredDate ? -1 : 1;
/**
 * Incidental costs of a trade, counted exactly once: brokerage (EX-GST) + other fees + GST on brokerage.
 * Contract: `brokerageCents` never includes GST. A "brokerage includes 10 % GST" toggle must be mapped with
 * `splitGstInclusiveBrokerage` before saving; previews and saves must both use this function.
 * ponytail: GST is always part of the cost base/selling costs; GST-registered entities claiming input tax credits
 * should record gstCents 0 (or net it out) — confirm per entity.
 */
export const tradeCosts = (t) => (t.brokerageCents ?? 0) + (t.feesCents ?? 0) + (t.gstCents ?? 0);
/** Split a GST-inclusive brokerage charge into ex-GST brokerage + GST (GST = total × rate / (1 + rate), half-even). */
export const splitGstInclusiveBrokerage = (totalCents, gstRate = 0.1) => {
    const gst = roundCents(dec(totalCents).mul(gstRate).div(1 + gstRate));
    return { brokerageCents: totalCents - gst, gstCents: gst };
};
/** New parcel from a buy. Cost base = gross + `tradeCosts(trade)`, converted to base currency at `trade.fxRate`. */
export const parcelFromBuy = (trade, baseCurrency = 'AUD') => {
    if (trade.side !== 'buy')
        throw new KeelError('VALIDATION', `parcelFromBuy needs a buy trade, got ${trade.side}`, { tradeId: trade.id });
    if (!dec(trade.units).gt(0))
        throw new KeelError('VALIDATION', 'Buy units must be positive', { tradeId: trade.id });
    const cost = toBaseCents(trade.grossCents + tradeCosts(trade), trade.currency, trade.fxRate, baseCurrency);
    const u = unitsToString(trade.units);
    return {
        id: `parcel:${trade.id}`, entityId: trade.entityId, accountId: trade.accountId, securityId: trade.securityId,
        acquiredDate: trade.date, originalTradeId: trade.id, originalUnits: u, remainingUnits: u,
        costBaseCents: cost, reducedCostBaseCents: cost, isPre1999: trade.date < AU_INDEXATION_CUTOFF,
        adjustments: [], closedDate: null, createdAt: trade.createdAt, updatedAt: trade.updatedAt,
    };
};
export const drpParcelId = (incomeEventId) => `parcel:drp:${incomeEventId}`;
/** New parcel from a dividend reinvestment: units = drpUnits, cost = drpPrice × units, acquired on the pay date. */
export const parcelFromDrp = (ev, baseCurrency = 'AUD') => {
    if (!ev.isDrp || ev.drpUnits === null || ev.drpPriceCents === null || !dec(ev.drpUnits).gt(0)) {
        throw new KeelError('VALIDATION', 'Income event is not a DRP with units and price', { incomeEventId: ev.id });
    }
    // drpPriceCents may be fractional (ASX DRP prices have 4 dp): convert the exact amount, round once.
    const cost = roundCents(toBaseDecimal(dec(ev.drpUnits).mul(ev.drpPriceCents), ev.currency, ev.fxRate, baseCurrency));
    const u = unitsToString(ev.drpUnits);
    return {
        id: drpParcelId(ev.id), entityId: ev.entityId, accountId: ev.accountId, securityId: ev.securityId,
        acquiredDate: ev.payDate, originalTradeId: null, originalUnits: u, remainingUnits: u,
        costBaseCents: cost, reducedCostBaseCents: cost, isPre1999: ev.payDate < AU_INDEXATION_CUTOFF,
        adjustments: [], closedDate: null, createdAt: ev.createdAt, updatedAt: ev.updatedAt,
    };
};
/** Per-unit CGT gain (+) / loss (−) if sold at `price`: loss measured against reduced cost base. */
const gainPerUnit = (p, price) => {
    const u = dec(p.remainingUnits);
    const cb = dec(p.costBaseCents).div(u), rcb = dec(p.reducedCostBaseCents).div(u);
    return price.gt(cb) ? price.minus(cb) : price.lt(rcb) ? price.minus(rcb) : dec(0);
};
const validateManual = (open, manual, units) => {
    const byId = new Map(open.map((p) => [p.id, p]));
    const seen = new Set();
    let total = dec(0);
    for (const a of manual) {
        const p = byId.get(a.parcelId);
        if (!p)
            throw new KeelError('VALIDATION', `Parcel ${a.parcelId} is not an open parcel of this security`, a);
        if (seen.has(a.parcelId))
            throw new KeelError('VALIDATION', `Parcel ${a.parcelId} allocated twice`, a);
        seen.add(a.parcelId);
        const u = dec(a.units);
        if (!u.gt(0))
            throw new KeelError('VALIDATION', 'Allocated units must be positive', a);
        if (u.gt(p.remainingUnits)) {
            throw new KeelError('INSUFFICIENT_UNITS', `Parcel ${p.id} has ${p.remainingUnits} units, ${a.units} requested`, a);
        }
        total = total.plus(u);
    }
    if (!total.eq(units))
        throw new KeelError('VALIDATION', `Manual allocation totals ${total.toFixed()} units, sale is ${units.toFixed()}`);
    return manual.map((a) => ({ parcelId: a.parcelId, units: unitsToString(a.units) }));
};
/** Choose which parcels a sale consumes. Throws INSUFFICIENT_UNITS when the open parcels can't cover `units`. */
export const selectParcels = (input) => {
    const want = dec(input.units);
    if (!want.gt(0))
        throw new KeelError('VALIDATION', 'Units to sell must be positive');
    const securities = new Set(input.parcels.map((p) => p.securityId));
    if (securities.size > 1)
        throw new KeelError('VALIDATION', 'selectParcels needs parcels of a single security', [...securities]);
    const open = input.parcels.filter((p) => isOpenParcel(p) && p.acquiredDate <= input.date);
    const available = open.reduce((s, p) => s.plus(p.remainingUnits), dec(0));
    if (available.lt(want)) {
        throw new KeelError('INSUFFICIENT_UNITS', `Only ${available.toFixed()} units held, ${want.toFixed()} requested`, {
            requested: want.toFixed(), available: available.toFixed(),
        });
    }
    if (input.strategy === 'manual')
        return validateManual(open, input.manual ?? [], want);
    const j = jurisdictionFor(input.ctx.jurisdiction);
    const base = input.ctx.baseCurrency ?? 'AUD';
    const price = toBaseDecimal(input.priceCents, input.currency ?? base, input.fxRate, base);
    const eligible = (p) => j.isDiscountEligible(p.acquiredDate, input.date);
    const cpu = (p) => dec(p.costBaseCents).div(p.remainingUnits);
    const key = (p) => {
        const g = gainPerUnit(p, price);
        switch (input.strategy) {
            case 'fifo':
            case 'lifo':
            case 'manual': return [];
            case 'highest_cost': return [cpu(p).neg()];
            case 'lowest_cost': return [cpu(p)];
            case 'max_loss': return [g];
            case 'max_discounted_gain': return [dec(eligible(p) ? 0 : 1), g.neg()];
            // estimated tax per unit (jurisdiction rate: AU discount, US long/short-term, GB flat); losses (negative) first
            case 'min_tax': return [g.gt(0) ? g.mul(j.marginalGainRate(input.ctx, eligible(p))) : g];
            default: return assertNever(input.strategy, 'strategy');
        }
    };
    const keyed = open.map((p) => ({ p, k: key(p) }));
    keyed.sort((a, b) => {
        for (let i = 0; i < a.k.length; i++) {
            const c = (a.k[i] ?? dec(0)).cmp(b.k[i] ?? dec(0));
            if (c !== 0)
                return c;
        }
        return input.strategy === 'lifo' ? -byDateThenId(a.p, b.p) : byDateThenId(a.p, b.p);
    });
    const out = [];
    let left = want;
    for (const { p } of keyed) {
        if (left.lte(0))
            break;
        const take = Decimal.min(left, p.remainingUnits);
        out.push({ parcelId: p.id, units: unitsToString(take) });
        left = left.minus(take);
    }
    return out;
};
/** Split `units` off a parcel: returns the apportioned cost bases and the parcel's remainder. */
export const sliceParcel = (p, units) => {
    const rem = dec(p.remainingUnits), s = dec(units);
    if (s.gt(rem))
        throw new KeelError('INSUFFICIENT_UNITS', `Parcel ${p.id} has ${p.remainingUnits} units, ${units} requested`);
    const all = s.eq(rem);
    const cb = all ? p.costBaseCents : roundCents(dec(p.costBaseCents).mul(s).div(rem));
    const rcb = all ? p.reducedCostBaseCents : roundCents(dec(p.reducedCostBaseCents).mul(s).div(rem));
    return {
        costBaseCents: cb, reducedCostBaseCents: rcb,
        rest: { ...p, remainingUnits: unitsToString(rem.minus(s)), costBaseCents: p.costBaseCents - cb, reducedCostBaseCents: p.reducedCostBaseCents - rcb },
    };
};
/** Consume allocations from parcels and emit one CgtEvent per allocation. Pure. */
export const disposeAllocations = (input) => {
    const j = jurisdictionFor(input.ctx.jurisdiction);
    const current = new Map(input.parcels.map((p) => [p.id, p]));
    const proceeds = allocateCents(input.netProceedsCents, input.allocations.map((a) => Number(a.units)));
    const cgtEvents = input.allocations.map((a, i) => {
        const p = current.get(a.parcelId);
        if (!p)
            throw new KeelError('VALIDATION', `Unknown parcel ${a.parcelId}`);
        const s = sliceParcel(p, a.units);
        const closed = dec(s.rest.remainingUnits).isZero();
        current.set(p.id, { ...s.rest, closedDate: closed ? input.date : p.closedDate, updatedAt: input.timestamp });
        return j.computeDisposal({ parcel: p, units: a.units, costBaseCents: s.costBaseCents, reducedCostBaseCents: s.reducedCostBaseCents,
            tradeId: input.tradeId, corporateActionId: input.corporateActionId, timestamp: input.timestamp }, proceeds[i] ?? 0, input.date, input.ctx);
    });
    return { parcels: input.parcels.map((p) => current.get(p.id) ?? p), cgtEvents };
};
/** Apply a sell trade to parcels using explicit allocations. Proceeds = gross − `tradeCosts(trade)` (base currency). */
export const applySell = (trade, parcels, allocations, ctx) => {
    if (trade.side !== 'sell')
        throw new KeelError('VALIDATION', `applySell needs a sell trade, got ${trade.side}`, { tradeId: trade.id });
    const eligible = parcels.filter((p) => p.securityId === trade.securityId && p.accountId === trade.accountId && p.entityId === trade.entityId);
    validateManual(eligible.filter(isOpenParcel), allocations, dec(trade.units));
    const net = trade.grossCents - tradeCosts(trade);
    return disposeAllocations({
        parcels, allocations, netProceedsCents: toBaseCents(net, trade.currency, trade.fxRate, ctx.baseCurrency ?? 'AUD'),
        date: trade.date, tradeId: trade.id, corporateActionId: null, timestamp: trade.updatedAt, ctx,
    });
};
//# sourceMappingURL=parcels.js.map